import { useState, useRef, useCallback, useEffect } from 'react'
import { useZinsserStore } from '@/stores/useZinsserStore'
import {
  Paperclip,
  Upload,
  X,
  FileText,
  Image,
  File,
  Loader2,
  AlertCircle,
  CheckCircle2,
} from 'lucide-react'
import {
  uploadZinsserAttachment,
  listZinsserAttachments,
  deleteZinsserAttachment,
  type Attachment,
} from '@/lib/api'

const FILE_TYPE_ICONS: Record<string, typeof FileText> = {
  text: FileText,
  pdf: File,
  image: Image,
}

const SIZE_LIMITS = {
  text: 1 * 1024 * 1024, // 1MB
  pdf: 10 * 1024 * 1024, // 10MB
  image: 5 * 1024 * 1024, // 5MB
}

const MAX_ATTACHMENTS = 5

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getFileType(mimeType: string): 'text' | 'pdf' | 'image' {
  if (mimeType === 'application/pdf') return 'pdf'
  if (mimeType.startsWith('image/')) return 'image'
  return 'text'
}

interface AttachmentCardProps {
  attachment: Attachment
  onDelete: (id: string) => void
  isDeleting: boolean
}

function AttachmentCard({ attachment, onDelete, isDeleting }: AttachmentCardProps) {
  const Icon = FILE_TYPE_ICONS[attachment.file_type] || File
  const isProcessing = attachment.extraction_status === 'pending'
  const hasFailed = attachment.extraction_status === 'failed'

  return (
    <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg group">
      <div className="flex-shrink-0 w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
        <Icon className="w-5 h-5 text-muted-foreground" strokeWidth={1.5} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">
            {attachment.original_filename}
          </span>
          {isProcessing && (
            <Loader2 className="w-3.5 h-3.5 text-amber-500 animate-spin flex-shrink-0" />
          )}
          {hasFailed && (
            <AlertCircle className="w-3.5 h-3.5 text-red-500 flex-shrink-0" />
          )}
          {attachment.extraction_status === 'completed' && (
            <CheckCircle2 className="w-3.5 h-3.5 text-green-500 flex-shrink-0" />
          )}
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <span>{formatFileSize(attachment.file_size_bytes)}</span>
          <span>•</span>
          <span className="capitalize">{attachment.file_type}</span>
          {hasFailed && attachment.extraction_error && (
            <>
              <span>•</span>
              <span className="text-red-500 truncate">{attachment.extraction_error}</span>
            </>
          )}
        </div>
      </div>

      <button
        onClick={() => onDelete(attachment.attachment_id)}
        disabled={isDeleting}
        className="flex-shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted transition-colors md:opacity-0 md:group-hover:opacity-100"
        aria-label="Delete attachment"
      >
        {isDeleting ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <X className="w-4 h-4" />
        )}
      </button>
    </div>
  )
}

export function ZinsserAttachmentsPanel() {
  const sessionId = useZinsserStore((state) => state.sessionId)
  const attachmentVersion = useZinsserStore((state) => state.attachmentVersion)
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Load attachments when session changes or attachmentVersion increments (paste upload)
  useEffect(() => {
    if (!sessionId) {
      setAttachments([])
      return
    }

    async function loadAttachments(id: string) {
      setIsLoading(true)
      // Don't set error for initial load - just show empty state
      try {
        const response = await listZinsserAttachments(id)
        setAttachments(response.attachments)
        setError(null) // Clear any previous error on success
      } catch (err) {
        // Silently fail on initial load - empty state is fine
        // Only log for debugging
        console.warn('Failed to load attachments (this is expected if migration not run):', err)
        setAttachments([])
      } finally {
        setIsLoading(false)
      }
    }

    loadAttachments(sessionId)
  }, [sessionId, attachmentVersion])

  const handleUpload = useCallback(
    async (files: FileList | null) => {
      if (!files || files.length === 0 || !sessionId) return

      // Check attachment limit
      if (attachments.length + files.length > MAX_ATTACHMENTS) {
        setError(`Maximum ${MAX_ATTACHMENTS} attachments allowed`)
        return
      }

      setIsUploading(true)
      setError(null)

      for (const file of Array.from(files)) {
        const fileType = getFileType(file.type)
        const sizeLimit = SIZE_LIMITS[fileType]

        // Validate file size
        if (file.size > sizeLimit) {
          setError(`${file.name} exceeds ${formatFileSize(sizeLimit)} limit for ${fileType} files`)
          continue
        }

        try {
          const attachment = await uploadZinsserAttachment(sessionId, file)
          setAttachments((prev) => [...prev, attachment])
        } catch (err) {
          console.error('Upload failed:', err)
          setError(err instanceof Error ? err.message : 'Upload failed')
        }
      }

      setIsUploading(false)
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    },
    [sessionId, attachments.length]
  )

  const handleDelete = useCallback(
    async (attachmentId: string) => {
      if (!sessionId) return

      setDeletingId(attachmentId)
      setError(null)

      try {
        await deleteZinsserAttachment(sessionId, attachmentId)
        setAttachments((prev) => prev.filter((a) => a.attachment_id !== attachmentId))
      } catch (err) {
        console.error('Delete failed:', err)
        setError(err instanceof Error ? err.message : 'Delete failed')
      } finally {
        setDeletingId(null)
      }
    },
    [sessionId]
  )

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragging(false)
  }, [])

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setIsDragging(false)
      handleUpload(e.dataTransfer.files)
    },
    [handleUpload]
  )

  const canUpload = attachments.length < MAX_ATTACHMENTS && !isUploading

  if (!sessionId) {
    return (
      <div className="h-full flex items-center justify-center text-center p-4">
        <div>
          <Paperclip className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" strokeWidth={1.5} />
          <p className="text-sm text-muted-foreground">
            Start a session to add attachments
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Upload zone */}
      <div
        className={`flex-shrink-0 m-3 p-4 border-2 border-dashed rounded-lg transition-colors ${
          isDragging
            ? 'border-primary bg-primary/5'
            : canUpload
            ? 'border-muted-foreground/25 hover:border-muted-foreground/50'
            : 'border-muted-foreground/10 bg-muted/20'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => handleUpload(e.target.files)}
          accept=".txt,.md,.csv,.json,.xml,.yaml,.yml,.js,.ts,.py,.pdf,.png,.jpg,.jpeg,.gif,.webp"
          disabled={!canUpload}
        />

        <div className="flex flex-col items-center gap-2 text-center">
          {isUploading ? (
            <>
              <Loader2 className="w-6 h-6 text-primary animate-spin" />
              <p className="text-sm text-muted-foreground">Uploading...</p>
            </>
          ) : (
            <>
              <Upload
                className={`w-6 h-6 ${canUpload ? 'text-muted-foreground' : 'text-muted-foreground/30'}`}
                strokeWidth={1.5}
              />
              <div>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={!canUpload}
                  className={`text-sm font-medium ${
                    canUpload
                      ? 'text-primary hover:underline cursor-pointer'
                      : 'text-muted-foreground/50 cursor-not-allowed'
                  }`}
                >
                  Click to upload
                </button>
                <span className="text-sm text-muted-foreground"> or drag and drop</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Text, PDF, or images ({attachments.length}/{MAX_ATTACHMENTS})
              </p>
            </>
          )}
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="flex-shrink-0 mx-3 mb-3 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
          <p className="text-xs text-red-500 flex items-center gap-1.5">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" />
            {error}
          </p>
        </div>
      )}

      {/* Attachments list */}
      <div className="flex-1 overflow-y-auto px-3 pb-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
          </div>
        ) : attachments.length > 0 ? (
          <div className="space-y-2">
            {attachments.map((attachment) => (
              <AttachmentCard
                key={attachment.attachment_id}
                attachment={attachment}
                onDelete={handleDelete}
                isDeleting={deletingId === attachment.attachment_id}
              />
            ))}
          </div>
        ) : (
          <div className="flex items-center justify-center text-center py-8">
            <div>
              <Paperclip
                className="w-6 h-6 text-muted-foreground/30 mx-auto mb-2"
                strokeWidth={1.5}
              />
              <p className="text-sm text-muted-foreground">No attachments yet</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                Add files for context during editing
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Info footer */}
      <div className="flex-shrink-0 px-3 py-2 border-t border-border">
        <p className="text-xs text-muted-foreground text-center">
          Files are processed on upload and inform the editing workflow
        </p>
      </div>
    </div>
  )
}
